import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

/**
 * FAQSection — Bloc FAQ réutilisable avec accordéon.
 * Les données sont aussi consommées par SEOHead (faqSchema) pour générer
 * le rich snippet FAQPage dans Google.
 *
 * @param {Object[]} items - [{ q, a }]
 * @param {string} title - titre de la section (H2)
 * @param {string} eyebrow - petite étiquette au-dessus
 * @param {string} testIdPrefix - préfixe pour data-testid (ex: "site-location")
 */
export default function FAQSection({
  items,
  title = "Questions fréquentes.",
  eyebrow = "— FAQ",
  testIdPrefix = "faq",
}) {
  const [open, setOpen] = useState(0);
  if (!items || !items.length) return null;

  return (
    <section
      className="mt-20 md:mt-24"
      data-testid={`${testIdPrefix}-section`}
      aria-labelledby={`${testIdPrefix}-title`}
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-semibold">
        {eyebrow}
      </div>
      <h2
        id={`${testIdPrefix}-title`}
        className="mt-2 font-outfit text-3xl md:text-4xl font-light tracking-tight"
      >
        {title}
      </h2>

      <div className="mt-8 rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-xl divide-y divide-white/5 overflow-hidden">
        {items.map((it, i) => {
          const isOpen = open === i;
          return (
            <div key={it.q} data-testid={`${testIdPrefix}-item-${i}`}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                className="w-full text-left flex items-start gap-4 px-5 md:px-7 py-5 hover:bg-white/[0.015] transition"
                data-testid={`${testIdPrefix}-toggle-${i}`}
              >
                <h3 className="flex-1 text-base md:text-lg font-medium text-white/90 leading-snug">
                  {it.q}
                </h3>
                <div
                  className={`shrink-0 mt-1 w-7 h-7 rounded-full border flex items-center justify-center transition ${
                    isOpen
                      ? "bg-[#E52431]/15 border-[#E52431]/40 text-[#FFB4BB] rotate-180"
                      : "bg-white/5 border-white/10 text-white/60"
                  }`}
                >
                  <ChevronDown size={14} />
                </div>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 md:px-7 pb-6 text-sm md:text-[15px] text-white/70 leading-relaxed whitespace-pre-line">
                      {it.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
}
